/**
 * Andamento del valore nel tempo, con una o due serie confrontabili.
 *
 * ## Le due deformazioni che questo grafico aveva, e che sono diverse fra loro
 *
 * 1. **`preserveAspectRatio="none"` su un viewBox fisso.** Il riquadro era
 *    `720 × 240` unità disegnato dentro un elemento largo `100%` e alto 240px:
 *    su un contenitore da 1560px la scala orizzontale valeva 2,17 e quella
 *    verticale 1. Tutto veniva stirato in larghezza — testo delle etichette,
 *    spessore dei tratti (verticali più grossi degli orizzontali), pallini
 *    diventati ellissi — e **di quanto dipendeva dalla larghezza della
 *    finestra**: da qui «a volte è stretchato in un modo o nell'altro».
 *    Rimedio: il viewBox segue la larghezza misurata, così un'unità SVG è un
 *    pixel e non c'è nessuna scala anisotropa da subire.
 *
 * 2. **L'asse X non misurava il tempo.** L'ascissa era `indice / (n − 1)`:
 *    punti equidistanti a prescindere dalle date. Una settimana ferma e un
 *    giorno di salto occupavano lo stesso spazio, e la pendenza — cioè la sola
 *    cosa che un grafico di andamento serve a leggere — era inventata. Ora
 *    l'ascissa è l'istante.
 *
 * Il confronto fra titoli e patrimonio del dettaglio di portafoglio non sta
 * più qui: ha un componente proprio (`PortfolioValueChart`, F-183), che
 * condivide con questo la misura della larghezza e la scala arrotondata
 * (`chart-scale.ts`).
 *
 * ⚠️ Qui i valori decimali diventano `number`. È l'unico punto in cui è
 * legittimo (ADR-0006): servono a calcolare coordinate in pixel, non a fare
 * aritmetica sul denaro. Gli importi mostrati a schermo — tooltip, assi,
 * tabella — sono sempre formattati a partire dalla stringa originale.
 */

import { useId, useMemo, useState } from 'react';
import { niceBounds, useMeasuredWidth } from './chart-scale';
import { formatCurrency, formatDate, formatDateTime } from '@/lib/format';
import './value-trend-chart.css';

export type TrendPoint = {
  date: string;
  /** Stringa decimale, come arriva dall'API. */
  value: string | null;
};

type Props = {
  points: readonly TrendPoint[];
  currency: string;
  /** Descrizione del grafico per gli screen reader. */
  label: string;
  /**
   * Serie con più punti nello stesso giorno (es. prezzi watchlist, più
   * rilevazioni/ora): tooltip e tabella nascosta mostrano anche l'orario,
   * altrimenti punti diversi risulterebbero indistinguibili al passaggio.
   * Gli assi restano solo data, per non affollare lo spazio disponibile.
   */
  showTime?: boolean;
};

const VIEW_HEIGHT = 240;
const TICK_COUNT = 4;

const PADDING_TOP = 16;
const PADDING_BOTTOM = 28;
const PADDING_LEFT = 64;
const PADDING_RIGHT = 16;

type Sample = { date: string; value: string; numeric: number; time: number };

/** I punti con un valore leggibile, con l'istante già risolto. */
function samplesOf(points: readonly TrendPoint[]): Sample[] {
  return points
    .map((point) => ({
      date: point.date,
      value: point.value ?? '',
      numeric: point.value === null ? NaN : Number(point.value),
      time: new Date(point.date).getTime(),
    }))
    .filter((sample) => Number.isFinite(sample.numeric) && Number.isFinite(sample.time));
}

export function ValueTrendChart({
  points,
  currency,
  label,
  showTime = false,
}: Props) {
  const formatPointDate = showTime ? formatDateTime : formatDate;
  const titleId = useId();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [figureRef, width] = useMeasuredWidth();

  const plotWidth = Math.max(1, width - PADDING_LEFT - PADDING_RIGHT);
  const plotHeight = VIEW_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

  const scene = useMemo(() => {
    const primary = samplesOf(points);
    if (primary.length === 0) return null;

    const times = primary.map((sample) => sample.time);
    const tMin = Math.min(...times);
    const tSpan = Math.max(...times) - tMin;
    const xOf = (time: number): number =>
      PADDING_LEFT + (tSpan === 0 ? plotWidth / 2 : ((time - tMin) / tSpan) * plotWidth);

    const values = primary.map((sample) => sample.numeric);
    const { lower, upper } = niceBounds(Math.min(...values), Math.max(...values));
    const range = upper - lower || 1;
    const yOf = (value: number): number => PADDING_TOP + plotHeight - ((value - lower) / range) * plotHeight;

    return {
      primary: primary.map((sample) => ({ ...sample, x: xOf(sample.time), y: yOf(sample.numeric) })),
      leftTicks: Array.from({ length: TICK_COUNT + 1 }, (_, index) => ({
        value: lower + ((upper - lower) / TICK_COUNT) * index,
        y: PADDING_TOP + plotHeight - (index / TICK_COUNT) * plotHeight,
      })),
    };
  }, [points, plotWidth, plotHeight]);

  if (!scene) {
    return (
      <p className="trend__empty">
        Nessuno snapshot con un valore: l'andamento comparirà dopo il primo import valorizzato.
      </p>
    );
  }

  const { primary, leftTicks } = scene;

  // Con un solo punto una linea non esiste. Mostrarne una piatta suggerirebbe
  // una stabilità che il dato non dimostra: meglio dire che manca il confronto.
  if (primary.length === 1) {
    const only = primary[0]!;
    return (
      <div className="trend__single">
        <span className="trend__single-value">{formatCurrency(only.value, currency)}</span>
        <span className="trend__single-date">al {formatPointDate(only.date)}</span>
        <p className="trend__single-hint">
          Un solo snapshot: l'andamento richiede almeno due import in date diverse.
        </p>
      </div>
    );
  }

  const linePath = primary
    .map((sample, index) => `${index === 0 ? 'M' : 'L'} ${sample.x} ${sample.y}`)
    .join(' ');
  const areaPath =
    `${linePath} L ${primary[primary.length - 1]!.x} ${PADDING_TOP + plotHeight}` +
    ` L ${primary[0]!.x} ${PADDING_TOP + plotHeight} Z`;

  const hovered = hoveredIndex === null ? null : primary[hoveredIndex];
  const first = primary[0]!;
  const last = primary[primary.length - 1]!;

  const numberFormat = (value: number): string =>
    new Intl.NumberFormat('it-IT', {
      notation: Math.abs(value) >= 10_000 ? 'compact' : 'standard',
      maximumFractionDigits: Math.abs(value) >= 10_000 ? 1 : 0,
    }).format(value);

  /** Il punto più vicino al puntatore: bersaglio più ampio del marker. */
  const handleMove = (event: React.MouseEvent<SVGSVGElement>): void => {
    const svg = event.currentTarget;
    const bounds = svg.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * width;

    let nearest = 0;
    let distance = Number.POSITIVE_INFINITY;
    primary.forEach((sample, index) => {
      const delta = Math.abs(sample.x - x);
      if (delta < distance) {
        distance = delta;
        nearest = index;
      }
    });
    setHoveredIndex(nearest);
  };

  return (
    <figure className="trend" ref={figureRef as React.RefObject<HTMLElement>}>
      <svg
        className="trend__svg"
        // ⚠️ Niente `preserveAspectRatio="none"`: il viewBox segue la larghezza
        // misurata, quindi un'unità è un pixel e non c'è scala da deformare.
        viewBox={`0 0 ${width} ${VIEW_HEIGHT}`}
        role="img"
        aria-labelledby={titleId}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <title id={titleId}>{label}</title>

        {leftTicks.map((tick) => (
          <g key={`left-${tick.y}`}>
            <line
              className="trend__grid"
              x1={PADDING_LEFT}
              x2={width - PADDING_RIGHT}
              y1={tick.y}
              y2={tick.y}
            />
            <text className="trend__axis-label" x={PADDING_LEFT - 10} y={tick.y + 4} textAnchor="end">
              {numberFormat(tick.value)}
            </text>
          </g>
        ))}

        <path className="trend__area" d={areaPath} />
        <path className="trend__line" d={linePath} />

        {hovered && (
          <line
            className="trend__crosshair"
            x1={hovered.x}
            x2={hovered.x}
            y1={PADDING_TOP}
            y2={PADDING_TOP + plotHeight}
          />
        )}

        {primary.map((sample, index) => (
          <circle
            key={sample.date}
            className={`trend__marker ${hoveredIndex === index ? 'trend__marker--active' : ''}`}
            cx={sample.x}
            cy={sample.y}
            r={hoveredIndex === index ? 6 : 4}
          />
        ))}

        <text className="trend__axis-label" x={first.x} y={VIEW_HEIGHT - 8} textAnchor="start">
          {formatDate(first.date)}
        </text>
        <text className="trend__axis-label" x={last.x} y={VIEW_HEIGHT - 8} textAnchor="end">
          {formatDate(last.date)}
        </text>
      </svg>

      {hovered && (
        <div className="trend__tooltip" style={{ left: `${(hovered.x / width) * 100}%` }} role="status">
          <span className="trend__tooltip-date">{formatPointDate(hovered.date)}</span>
          <span className="trend__tooltip-value">{formatCurrency(hovered.value, currency)}</span>
        </div>
      )}

      {/* Vista tabellare: l'informazione non è mai solo nella forma del grafico. */}
      <table className="visually-hidden">
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">{showTime ? 'Data e ora' : 'Data'}</th>
            <th scope="col">Valore</th>
          </tr>
        </thead>
        <tbody>
          {primary.map((sample) => (
            <tr key={sample.date}>
              <td>{formatPointDate(sample.date)}</td>
              <td>{formatCurrency(sample.value, currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
